/**
 * bulk-delete-failures — turns the per-item results of the bulk
 * permanent-delete endpoints into a human summary for the toast.
 *
 * The server refuses items per-item with a machine `code` rather than
 * failing the whole batch; the UI's job is to translate those codes
 * into a reason the user can act on ("linked to deals" → detach or
 * delete the deal first).
 */

import type { FiasLeadsBulkDeleteResult } from '@/lib/api';

const REASON_BY_CODE: Record<string, string> = {
  CONTACT_REFERENCED_BY_DEALS: 'linked to deals',
  COMPANY_REFERENCED_BY_DEALS: 'linked to deals',
  COMPANY_HAS_RENEWALS: 'managed in Fias 360',
  CONTACT_NOT_ARCHIVED: 'not archived',
  COMPANY_NOT_ARCHIVED: 'not archived',
  // The delete verbs are owner/admin-gated; an org member with plain
  // write access reaches the endpoint but every item is refused.
  VERB_ROLE_FORBIDDEN: 'blocked — deleting requires an owner or admin role',
};

export interface BulkDeleteSummary {
  deleted: number;
  failed: number;
  /** e.g. "2 couldn't be deleted: 1 linked to deals, 1 managed in Fias 360."
   * Empty string when nothing failed. */
  failureDescription: string;
}

export function summarizeBulkDelete(
  results: readonly FiasLeadsBulkDeleteResult[],
): BulkDeleteSummary {
  const failures = results.filter((r) => !r.ok);
  const deleted = results.length - failures.length;
  if (failures.length === 0) {
    return { deleted, failed: 0, failureDescription: '' };
  }
  const byReason = new Map<string, number>();
  for (const f of failures) {
    const reason = (f.code && REASON_BY_CODE[f.code]) || 'could not be deleted';
    byReason.set(reason, (byReason.get(reason) ?? 0) + 1);
  }
  const parts = [...byReason.entries()].map(([reason, n]) => `${n} ${reason}`);
  return {
    deleted,
    failed: failures.length,
    failureDescription: `${failures.length} couldn't be deleted: ${parts.join(', ')}.`,
  };
}
